import { template } from 'lodash';
import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../core/controls/button/Button';
import { useTranslate } from '../../../core/translate';
import {
  computeDefaultAngle,
  computeImageSize,
} from '../../common/cgi/cgiHelpers';
import Footer from '../../components/footer/Footer';
import { PrintButton } from '../../steps/summary/Summary';
import useLargeBreakpoint from '../../hooks/useLargeBreakpoint';

const Confirmation = ({
  reservation,
  config,
  cgiSize,
  exteriorAngles,
  interiorAngles,
  wheelAngles,
  breakpoint,
  bodyStyleTitle,
}) => {
  const {
    id: confirmationId,
    dealer,
    urls,
    label,
    header,
    description,
  } = reservation;
  const translate = useTranslate();
  const navigate = useNavigate();

  const isAtLeastLarge = useLargeBreakpoint();

  const DealerInformation = () => {
    const {
      title: dealerName,
      address: dealerAddress,
      phone: dealerPhone,
      businessHours,
    } = dealer;
    const { street, city, state, zipcode } = dealerAddress;

    return (
      <div className="confirmation__dealer-information-container">
        <div className="confirmation__dealer-disclaimer">{description}</div>
        <div className="confirmation__dealer-information">
          <div className="confirmation__dealer-location">
            <span className="confirmation__dealer-name">{dealerName}</span>
            <div className="confirmation__dealer-address">
              {street}
              <br />
              {city}, {state} {zipcode}
              <br />
              {dealerPhone}
            </div>
          </div>
          <div className="confirmation__dealer-hours-container">
            <span className="confirmation__hours-label">
              {translate('stp.confirmation.ev.hours.label.v3')}
            </span>
            <div className="confirmation__dealer-hours">
              {businessHours.map(({ day, hours }) => {
                return (
                  <Fragment key={day}>
                    <div className="confirmation__dealer-hours__day">{day}</div>
                    <div className="confirmation__dealer-hours__hours">
                      {hours}
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const analyticsKey = 'order confirmation';

  const ConfirmationCtas = () => (
    <div className="confirmation__ctas">
      {urls.map((url, index) =>
        index === 1 ? (
          <Button
            key={url.key}
            primary
            className={'confirmation__cta--primary stat-button-link'}
            data-dtm={analyticsKey}
            onClick={() => {
              if (url.key === 'order.summary') {
                navigate('../summary');
              }
            }}>
            {url.label}
          </Button>
        ) : (
          <Button
            key={url.key}
            secondary
            className={'confirmation__cta--secondary stat-button-link'}
            data-dtm={analyticsKey}
            onClick={() => {
              window.location.href = url.url;
            }}>
            {url.label}
          </Button>
        )
      )}
    </div>
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const { exterior } = config;
  const { cgiImageUrl: exteriorCgiImageUrl } = exterior;
  const imageSize = computeImageSize(breakpoint, cgiSize);

  const defaultExteriorAngle = computeDefaultAngle(
    'default',
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    false
  );

  const exteriorCgiUrl = template(exteriorCgiImageUrl)({
    imageSize,
    degree: defaultExteriorAngle,
    removeCat: '',
  });

  return (
    <div className="confirmation__view">
      <div className="confirmation__container">
        <div className="confirmation__header">{header}</div>
        <div className="confirmation__content-container">
          <img
            src={exteriorCgiUrl}
            alt={bodyStyleTitle}
            className="confirmation__image"
          />
          <div className="confirmation__request-information">
            <div className="confirmation__request-header">
              {label && (
                <div>
                  <span className="confirmation__request-label">{label}</span>
                  {isAtLeastLarge && (
                    <span className="confirmation__request-number">{` ${confirmationId}`}</span>
                  )}
                </div>
              )}
              <PrintButton
                className="summary__links__button--confirmation"
                analyticsKey={analyticsKey}
              />
            </div>
            {label && !isAtLeastLarge && (
              <div className="confirmation__request-number">{`${confirmationId}`}</div>
            )}
            <DealerInformation />
            <ConfirmationCtas />
          </div>
        </div>
      </div>
      <div className="gridview__footer-section">
        <Footer className="gridview__footer" hideDealerInfo />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const { reservation, config, appResources, breakpoint } = state;
  const {
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    bodyStyleTitle,
  } = appResources;
  return {
    reservation,
    config,
    cgiSize,
    exteriorAngles,
    interiorAngles,
    wheelAngles,
    breakpoint,
    bodyStyleTitle,
  };
};

export default connect(mapStateToProps)(Confirmation);
